/** Debug overlay rendering world-space AI state labels above each enemy. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { crispText } from '../../ui/CrispUtil';
import { TextStyle, DEBUG_WORLD_ALPHA } from '../../ui/Theme';

const STATE_LABEL_OFFSET_Y = -34;

const STATE_COLORS: Record<string, string> = {
  duty: '#00cc00', alert: '#cccc00', chase: '#cc2222', search: '#cccc00',
  using_skill: '#ff4444', stagger: '#8888cc',
};

const STATE_DISPLAY: Record<string, string> = {
  duty: 'DUTY', alert: 'ALERT', chase: 'CHASE', search: 'SEARCH',
  using_skill: 'SKILL', stagger: 'STAGGER',
};

/** Pooled text labels showing each enemy's current AI state. Reuses Text objects to avoid GPU texture churn. */
export class StateLabelsOverlay {
  private scene: Phaser.Scene;
  private pool: Phaser.GameObjects.Text[] = [];
  private activeCount = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Update pooled labels above each enemy for the current frame. */
  draw(_graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    let idx = 0;

    for (const enemy of enemies) {
      const state = enemy.ai.state;
      let displayName = STATE_DISPLAY[state] ?? state.toUpperCase();
      if (state === 'duty') {
        const sub = enemy.ai.dutySubstate.toUpperCase();
        displayName = `DUTY:${sub}`;
      }
      const color = STATE_COLORS[state] ?? '#aaaaaa';

      const label = this.getOrCreate(idx);
      if (label.text !== displayName) label.setText(displayName);
      label.setPosition(enemy.sprite.x, enemy.sprite.y + STATE_LABEL_OFFSET_Y);
      if (label.style.color !== color) label.setColor(color);
      label.setVisible(true);
      idx++;
    }

    for (let i = idx; i < this.activeCount; i++) {
      this.pool[i].setVisible(false);
    }
    this.activeCount = idx;
  }

  private getOrCreate(index: number): Phaser.GameObjects.Text {
    if (index < this.pool.length) return this.pool[index];
    const label = crispText(this.scene, 0, 0, '', TextStyle.debugWorld)
      .setOrigin(0.5).setDepth(2001).setAlpha(DEBUG_WORLD_ALPHA);
    this.pool.push(label);
    return label;
  }

  /** Hide all active labels. */
  clear(): void {
    for (let i = 0; i < this.activeCount; i++) {
      this.pool[i].setVisible(false);
    }
    this.activeCount = 0;
  }

  /** Destroy all pooled labels and free resources. */
  destroy(): void {
    for (const label of this.pool) label.destroy();
    this.pool = [];
    this.activeCount = 0;
  }
}
